import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import React, { useMemo, useState } from "react";
import styles from "./VersioningDocumentRow.module.css";
import VersioningDocumentList from "./VersioningDocumentList";
import { isNonEmptyArray } from "../../../../../../common/utils";
import { CircularLoader, CustomTooltip, icon, IconComponent } from "../../../../../../common/components";
import { translate } from "../../../../../../common/providers";

let allowExpand = true;

const getDocCountRecursively = (folders, counter = 0) => {
	if (!isNonEmptyArray(folders)) {
		return null;
	}
	let temp = counter;
	temp = folders.filter((d) => !isNonEmptyArray(d.documents))?.length || 0;
	folders.forEach((folder) => {
		temp += getDocCountRecursively(folder.documents, temp);
	});

	return temp;
};

const VersioningDocumentRow = ({
	containerWidth,
	document,
	indentLevel = 0,
	isSelected,
	mode,
	onCheck,
	onToggleExpand,
	selectedDocId,
	selectedRowId,
}) => {
	const [isLoading, setIsLoading] = useState(false);

	const childrenCount = useMemo(() => {
		if (document.childrenCount >= 0) {
			return document.childrenCount;
		}
		if (document.count >= 0) {
			return document.count;
		}
		if (isNonEmptyArray(document.documents)) {
			if (mode === "folder") {
				return getDocCountRecursively(document.documents);
			}

			return document.documents?.filter((d) => d.type !== "FOLDER")?.length;
		}
		return undefined;
	}, [document, mode]);

	const isExpandable = useMemo(
		() => childrenCount >= 0 || isNonEmptyArray(document.documents),
		[childrenCount, document]
	);

	const handleExpand = () => {
		if (!isExpandable || childrenCount === 0) {
			return;
		}
		setTimeout(() => {
			if (allowExpand) {
				setIsLoading(true);
				onToggleExpand(document, setIsLoading);
			} else {
				allowExpand = true;
			}
		}, 0);
	};
	const handleClick = (e) => {
		if (typeof e.stopPropagation === "function") {
			e.stopPropagation();
		}
		if (document.type === "FOLDER") {
			handleExpand();
			return;
		}
		onCheck(document);
	};
	const iconRenderer = () => {
		if (isExpandable) {
			return (
				<IconComponent
					className={styles.icon}
					color="var(--blue-200)"
					icon={document.expanded ? icon.faFolderOpen : icon.faFolder}
					size="2xs"
				/>
			);
		}
		return <IconComponent className={styles.icon} color="var(--grey-400)" icon={icon.faFile} size="sm" />;
	};
	return (
		<Accordion
			classes={{
				root: styles.accordion__root,
			}}
			expanded={!!document.expanded || document.filtered !== undefined}
			style={{
				marginLeft: indentLevel > 0 ? 12 : 0,
				width: "100%",
			}}
			onChange={handleExpand}
		>
			<CustomTooltip
				className={styles.emptyTooltip}
				placement="bottom-start"
				title={childrenCount === 0 ? translate("common:filters.document-tree.empty-folder") : ""}
			>
				<AccordionSummary
					classes={{
						root: styles.accordionSummary__root,
						content: `${styles.accordionSummary__content} ${
							childrenCount === 0 ? styles["accordionSummary__content--disabled"] : ""
						}`,
					}}
					style={{
						cursor: "pointer",
					}}
				>
					{isExpandable && (
						<>
							{(isLoading && <CircularLoader className={styles.row__loader} size={18} />) || (
								<>
									<IconComponent
										className={styles.expandIcon}
										color="var(--grey-400)"
										data-testid="docTreeExpand"
										icon={
											((document.expanded || document.filtered !== undefined) &&
												icon.faChevronDown) ||
											icon.faChevronRight
										}
										size="sm"
									/>
								</>
							)}
						</>
					)}
					<div className={styles.row__text} role="presentation" onClick={handleClick}>
						<div className={styles.documentRowWrapper} data-isFolder={document.type === "FOLDER"}>
							<div
								className={`${styles.documentRow} ${
									isSelected && document.type !== "FOLDER" ? styles["documentRow--selected"] : ""
								}`}
								data-isFolder={document.type === "FOLDER"}
							>
								<span className={`${styles.documentName}`} type="button">
									{iconRenderer()}
									<CustomTooltip showWhenOverflow title={document.name || document.label}>
										<span
											className={`${styles.fileName} ${
												isSelected && document.type !== "FOLDER"
													? styles["fileName--selected"]
													: ""
											}`}
										>
											{document.name || document.label}
										</span>
									</CustomTooltip>
								</span>
							</div>
						</div>
					</div>
				</AccordionSummary>
			</CustomTooltip>
			<AccordionDetails
				classes={{
					root: styles.accordionDetails__root,
				}}
			>
				{isNonEmptyArray(document.documents) && (
					<VersioningDocumentList
						containerWidth={containerWidth}
						documents={document.documents}
						indentLevel={indentLevel + 1}
						mode={mode}
						selectedDocId={selectedDocId}
						selectedRowId={selectedRowId}
						onCheck={onCheck}
						onToggleExpand={onToggleExpand}
					/>
				)}
			</AccordionDetails>
		</Accordion>
	);
};

export default VersioningDocumentRow;
